import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Grid,
} from "@mui/material";
import PersistentDataGrid from "../../components/table/PersistentDataGrid";
import { GridToolbar } from "@mui/x-data-grid";
import ServicingApi from "../../services/AssociatedServices";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import translations from "../../assets/locals/translations";

const AssociatedServices = () => {
  const Services = new ServicingApi();
  const { state } = useLocation();
  const item = state?.item || {};
  const currentLanguage = useSelector((state) => state.language.language);
  const t = translations[currentLanguage] || translations['en'];
  const [servicesData, setServicesData] = useState([]);
  const [deliveryCount, setDeliveryCount] = useState(0);
  const [itlCount, setItlCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const pageSizeKey = `rowsPerPage_${window.location.pathname}`;
  const [pageSize, setPageSize] = useState(() => {
    const saved = sessionStorage.getItem(pageSizeKey);
    return saved ? parseInt(saved, 10) : 50;
  });
  const [openFollowUp, setOpenFollowUp] = useState(false);
  const [openRecordService, setOpenRecordService] = useState(false);
  const [serviceDate, setServiceDate] = useState("");

  // Follow Up form states
  const [followUpNote, setFollowUpNote] = useState("");
  const [nextFollowUpDate, setNextFollowUpDate] = useState("");

  const [serviceNotes, setServiceNotes] = useState("");

  const theme = useTheme();
  // Adjust grid container based on screen size for responsiveness
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  // Record Service form states
  const [serviceName, setServiceName] = useState("");
  const [lastServiceHours, setLastServiceHours] = useState("");
  const [totalHours, setTotalHours] = useState("");
  const [followUpHistory, setFollowUpHistory] = useState([]);

  // Global search state
  const [searchQuery, setSearchQuery] = useState("");

  // Column visibility state
  const [associatedServicesColumnShowHideModel, setAssociatedServicesColumnShowHideModel] = useState({});
  const handleAssociatedServicesColumnShowHideChange = (newModel) => {
    setAssociatedServicesColumnShowHideModel(newModel);
    localStorage.setItem('associatedServices_columnShowHide', JSON.stringify(newModel));
  };

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem('associatedServices_columnShowHide'));
    if (saved) {
      setAssociatedServicesColumnShowHideModel(saved);
    }
  }, []);

  // Handlers for form submission
  const handleFollowUpSubmit = async () => {
    const res = await Services.addServiceFollowup({
      customer_id: selectedVehicle.id,
      service_id: item.service_id,
      notes: followUpNote,
      next_followup_date: nextFollowUpDate,
    });
    if (res.isSuccess && res.Followup) {
      setFollowUpHistory(res.Followup);
    }
  };

  const handleFollowupModel = async () => {
    setOpenFollowUp(true);
    const res = await Services.getServiceFollowup(
      selectedVehicle.id,
      item.service_id
    );
    if (res.isSuccess && res.Followup) {
      setFollowUpHistory(res.Followup);
    }
  };
  const closeFollowUp = () => {
    setOpenFollowUp(false);
    setFollowUpHistory([]);
    setFollowUpNote("");
    setNextFollowUpDate("");
  };
  const closeOpenService = () => {
    setServiceName("");
    setLastServiceHours("");
    setTotalHours("");
    setOpenRecordService(false);
  };
  const handleServiceSubmit = () => {
    // Clear the fields and close modal
    setServiceName("");
    setLastServiceHours("");
    setTotalHours("");
    setOpenRecordService(false);
  };

  const getServicingVehicles = async (
    service_id,
    hours_threshold,
    days_threshold
  ) => {
    setLoading(true);
    try {
      const response = await Services.getServicingVehicles({
        service_id,
        hours_threshold,
        days_threshold,
      });
      if (response && response.isSuccess) {
        const rowData = response.result.map((item, index) => ({
          ...item,
          no: index + 1,
        }));
        setServicesData(rowData);

        // Extract counts from the first row (all rows have same window values)
        if (rowData.length > 0) {
          setDeliveryCount(rowData[0].delivery_count || 0);
          setItlCount(rowData[0].itl_count || 0);
        } else {
          setDeliveryCount(0);
          setItlCount(0);
        }
      }
    } catch (error) {
      console.error(error, "Error getting servicing list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (item) {
      getServicingVehicles(
        item.service_id,
        item.hours_threshold,
        item.days_threshold
      );
    }
  }, [item]);

  const columns = [
    { field: "no", headerName: t.no || "No", flex: 1, minWidth: 70 },
    {
      field: "service_name",
      headerName: t.serviceMilestone || "Service Milestone",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <span>{params.value ? params.value.toUpperCase() : ""}</span>
      ),
    },
    {
      field: "customer_name",
      headerName: t.customerName || "Customer Name",
      flex: 1,
      minWidth: 310,
      renderCell: (params) => (
        <span>{params.value ? params.value.toUpperCase() : ""}</span>
      ),
    },
    {
      field: "due_date",
      headerName: t.dueDate || "Due Date",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => {
        if (!params.value) return "";
        const date = moment(params.value);
        return date.isValid() ? date.format("DD-MM-YYYY") : "";
      },
    },
    { field: "model_name", headerName: t.modal || "MODAL", flex: 1, minWidth: 180 },
    { field: "chassis_no", headerName: t.chassis || "CHASSIS", flex: 1, minWidth: 200 },
    { field: "engine_no", headerName: t.engine || "ENGINE", flex: 1, minWidth: 200 },
    { field: "last_hour", headerName: t.lastHours || "Last Hours", flex: 1, minWidth: 150 },
    { field: "total_hour", headerName: t.totalHours || "Total Hours", flex: 1, minWidth: 150 },

    {
      field: "delivery_date",
      headerName: t.deliveryDate || "Delivery Date",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const date = moment(params.row.delivery_date);
        return <span>{date.isValid() ? date.format("YYYY-MM-DD") : ""}</span>;
      },
    },
  ];
  // Handler to open the modal when engineNumber is clicked
  const handleEngineNumberClick = (vehicleRow) => {
    setSelectedVehicle(vehicleRow);
  };
  // Handler to close the modal
  const handleCloseModal = () => {
    setSelectedVehicle(null);
  };
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    try { sessionStorage.setItem(pageSizeKey, String(newPageSize)); } catch (e) { }
  };

  // Filter rows based on search query across multiple fields
  const filteredRows = useMemo(() => {
    if (!searchQuery) return servicesData;
    return servicesData.filter((row) =>
      Object.keys(row).some((field) =>
        String(row[field]).toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, servicesData]);

  return (
    <div className="bg-white p-2">
      {/* Header with title, counts, and global search input */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: isMobile ? 1 : 0,
            fontWeight: "bold",
            color: "#333", // adjust to your desired color
            textAlign: "center", // centers the text
            letterSpacing: "0.5px", // adds spacing between letters
            textTransform: "uppercase", // optional: transforms text to uppercase
          }}
        >
          {item.subTitle} <span>({item.title})</span>
        </Typography>

        {/* Counts for Delivery and ITL */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "flex-start" : "center",
            gap: 1,
            mb: isMobile ? 1 : 0,
            mr: isMobile ? 0 : 2,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t.delivery || "Delivery"}: {deliveryCount}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            ITL: {itlCount}
          </Typography>
        </Box>

        <TextField
          variant="outlined"
          placeholder={t.globalSearch || "Global Search..."}
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: isMobile ? "100%" : 300 }}
        />
      </Box>

      <div
        className="tableMenuHover"
        style={{ flexGrow: 1, width: "100%", height: "calc(100vh - 70px)" }}
      >
        <PersistentDataGrid
          rows={filteredRows}
          columns={columns}
          columnVisibilityModel={associatedServicesColumnShowHideModel}
          onColumnVisibilityModelChange={handleAssociatedServicesColumnShowHideChange}
          getRowId={(row) => row.no}
          pagination
          pageSize={pageSize}
          pageSizeOptions={[10, 15, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
          rowsPerPageOptions={[10, 20, 25, 50, 100]}
          onPageSizeChange={handlePageSizeChange}
          disableSelectionOnClick
          autoHeight={isMobile}
          components={{
            Toolbar: GridToolbar,
            NoRowsOverlay: () => (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <span style={{ fontFamily: "Poppins" }}>{t.noDataAvailable || "No Data Available"}</span>
              </div>
            ),
          }}
          componentsProps={{
            toolbar: {
              position: "right",
              style: { fontFamily: "Poppins", alignSelf: "end" },
            },
          }}
          sx={{
            width: "100%",
            "& .MuiDataGrid-columnHeaderTitle": {
              textTransform: "uppercase",
            },
          }}
          loading={loading}
        />
      </div>

      {/* Vehicle Detail Modal */}
      <Dialog
        open={Boolean(selectedVehicle)}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 1, // Slight rounding to match a modern UI
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
          },
        }}
        components={{
          Toolbar: GridToolbar,
          NoRowsOverlay: () => (
            <div
              style={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <span style={{ fontFamily: "Poppins" }}>No Data Available</span>
            </div>
          ),
        }}
        loading={loading}
      >
        {selectedVehicle && (
          <>
            <DialogTitle
              sx={{
                fontWeight: "bold",
                fontSize: "1rem",
                borderBottom: "1px solid #eee",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {selectedVehicle.engine_no} ({selectedVehicle.model_name})
              <IconButton
                aria-label="close"
                onClick={handleCloseModal}
                sx={{
                  color: "grey.500",
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ backgroundColor: "#fff", pt: 2, pb: 2 }}>
              {/* Two-column layout using table-like elements */}
              <Box
                component="table"
                sx={{
                  width: "100%",
                  borderCollapse: "collapse",
                  mb: 2,
                }}
              >
                <Box component="tbody">
                  <Box component="tr">
                    <Box
                      component="td"
                      sx={{ verticalAlign: "top", paddingTop: "8px" }}
                    >
                      Engine #: {selectedVehicle.engine_no}
                      <hr />
                    </Box>
                    <Box
                      component="td"
                      sx={{ verticalAlign: "top", paddingTop: "8px" }}
                    >
                      Chassis #: {selectedVehicle.chassis_no}
                      <hr />
                    </Box>
                  </Box>
                  <Box component="tr">
                    <Box component="td" sx={{ verticalAlign: "top" }}>
                      Model: {selectedVehicle.model_name}
                      <hr />
                    </Box>
                    <Box component="td" sx={{ verticalAlign: "top" }}>
                      Delivery Date:{" "}
                      {moment(selectedVehicle.delivery_date).format(
                        "YYYY-MM-DD"
                      )}
                      <hr />
                    </Box>
                  </Box>
                  <Box component="tr">
                    <Box component="td" sx={{ verticalAlign: "top" }}>
                      Last Service Hours: {selectedVehicle.last_service_hours}
                      <hr />
                    </Box>
                    <Box component="td" sx={{ verticalAlign: "top" }}>
                      Total Hours: {selectedVehicle.total_hours}
                      <hr />
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Instead of Service History, display two buttons */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-around", // Ensures max spacing between buttons
                  mt: 2,
                  width: "100%", // Ensure it stretches fully if needed
                }}
              >
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#4CAF50", // Change to your preferred color
                    color: "white",
                    textTransform: "none",
                    mr: 1,
                    "&:hover": { backgroundColor: "#388E3C" }, // Optional hover effect
                  }}
                  onClick={handleFollowupModel}
                >
                  {t.followUp || "Follow Up"}
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#FF9800",
                    color: "white",
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#F57C00" },
                  }}
                  onClick={() => setOpenRecordService(true)}
                >
                  {t.recordService || "Record Service"}
                </Button>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Follow Up Modal */}
      <Dialog
        open={openFollowUp}
        onClose={closeFollowUp}
        fullWidth
        maxWidth="sm"
        sx={{ "& .MuiPaper-root": { borderRadius: "12px" } }}
      >
        <DialogTitle
          sx={{ fontWeight: "bold", fontSize: "1.25rem", color: "#333" }}
        >
          {t.serviceFollowUp || "Service Follow-up"}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#f9f9f9", pt: 2, pb: 2 }}>
          <Typography sx={{ mb: 1, color: "#555" }}>
            <strong>{t.customer || "Customer"}:</strong> {selectedVehicle?.customer_name || "N/A"}
          </Typography>
          <Typography sx={{ mb: 2, color: "#555" }}>
            <strong>{t.phone || "Phone"}:</strong> {selectedVehicle?.phone_number || "N/A"}
          </Typography>
          <TextField
            label={t.followUpNote || "Follow-up Note"}
            fullWidth
            multiline
            rows={3}
            value={followUpNote}
            onChange={(e) => setFollowUpNote(e.target.value)}
            sx={{ mb: 2, backgroundColor: "#fff", borderRadius: "8px" }}
          />
          <TextField
            label={t.nextFollowUpDate || "Next Follow-up Date"}
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={nextFollowUpDate}
            onChange={(e) => setNextFollowUpDate(e.target.value)}
            sx={{ mb: 2, backgroundColor: "#fff", borderRadius: "8px" }}
          />

          {followUpHistory?.length > 0 && (
            <>
              <Typography sx={{ fontWeight: "bold", mt: 2, color: "#444" }}>
                {t.followUpHistory || "Follow-up History"}
              </Typography>
              <List
                sx={{
                  maxHeight: 200,
                  overflowY: "auto",
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  p: 1,
                }}
              >
                {followUpHistory.map((followUp, index) => (
                  <ListItem
                    key={index}
                    sx={{ borderBottom: "1px solid #ddd", pb: 1 }}
                  >
                    <ListItemText
                      primary={
                        <>
                          <Typography sx={{ color: "#333", fontWeight: "500" }}>
                            {`${t.date || "Date"}: ${moment(followUp.created_at).format(
                              "YYYY-MM-DD"
                            )}`}
                          </Typography>
                          <Typography sx={{ color: "#333", fontWeight: "500" }}>
                            {`${t.nextFollowUp || "Next Follow-Up"}: ${moment(
                              followUp.next_followup_date
                            ).format("YYYY-MM-DD")}`}
                          </Typography>
                        </>
                      }
                      secondary={
                        <>
                          <Typography
                            sx={{ color: "#666" }}
                          >{`${t.note || "Note"}: ${followUp.notes}`}</Typography>
                          <Typography sx={{ color: "#666" }}>{`${t.followedBy || "Followed By"}: ${followUp.follow_up_by_name || "N/A"
                            }`}</Typography>
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-around", p: 2 }}>
          <Button
            onClick={() => setOpenFollowUp(false)}
            variant="outlined"
            sx={{ borderRadius: "8px", textTransform: "none" }}
          >
            {t.cancel || "Cancel"}
          </Button>
          <Button
            onClick={handleFollowUpSubmit}
            variant="contained"
            color="primary"
            sx={{ borderRadius: "8px", textTransform: "none" }}
          >
            {t.submit || "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Record Service Modal */}
      <Dialog
        open={openRecordService}
        onClose={closeOpenService}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>{t.recordService || "Record Service"}</DialogTitle>
        <DialogContent sx={{ backgroundColor: "#fff", pt: 2, pb: 2 }}>
          <Grid container spacing={2}>
            {/* Service Name (read-only) */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                value={`${item.subTitle} ${item.title}`}
                disabled
                sx={{ mb: 2 }}
              />
            </Grid>
            {/* Two per row */}

            <Grid item xs={6}>
              <TextField
                label={t.lastServiceHours || "Last Service Hours"}
                fullWidth
                type="text"
                value={lastServiceHours}
                onChange={(e) => setLastServiceHours(e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t.totalHours || "Total Hours"}
                fullWidth
                type="text"
                value={totalHours}
                onChange={(e) => setTotalHours(e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t.serviceDate || "Service Date"}
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={serviceDate}
                onChange={(e) => setServiceDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t.serviceNote || "Service note..."}
                fullWidth
                multiline
                rows={3}
                value={serviceNotes}
                onChange={(e) => setServiceNotes(e.target.value)}
                sx={{ mb: 2, backgroundColor: "#fff", borderRadius: "8px" }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-around", p: 2 }}>
          <Button
            onClick={closeOpenService}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              transition: "background-color 0.3s, border-color 0.3s",
              borderColor: "error.main",
              color: "error.main",
              "&:hover": {
                backgroundColor: "error.light",
                borderColor: "error.dark",
                color: "white",
              },
            }}
          >
            {t.cancel || "Cancel"}
          </Button>
          <Button
            onClick={handleServiceSubmit}
            variant="contained"
            color="primary"
            sx={{ borderRadius: "8px", textTransform: "none" }}
          >
            {t.submit || "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AssociatedServices;
